import { HomeOutlined } from "@ant-design/icons";
import { Breadcrumb, message, Spin } from "antd";
import type { InferGetServerSidePropsType, NextPage } from "next";
import { GetServerSideProps } from "next";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useState } from "react";
import style from "./index.module.scss";
import { IReduxUserState } from "@/modules/user/redux/reducer";
import _ from "lodash";
import ModuleTM from "@/modules/tm";
import TMModel from "@/modules/tm/api/tm/tm-model";
import { useAppSelector } from "@/common/redux";
import HYHead from "@/common/components/head";
import AppRouter from "@/common/app/router";
import TMTransferNotFound from "@/modules/tm/components/transfer/not-found";
import TMTransferRecommend from "@/modules/tm/components/transfer/recommend";
import TMSearchTopInfo from "@/modules/tm/components/transfer/search-panel/components/top-info";
import TMDetailHeaderInfo from "@/modules/tm/components/detail/header-info";
import TMDetailBasicInfo from "@/modules/tm/components/detail/basic-info";
import TMDetailTransferData from "@/modules/tm/components/detail/transfer-data";
import PageHeader from "@/common/components/page-header";
import PageFooter from "@/common/components/page-footer";

const Api = ModuleTM.Api;

export interface ITMDetailCtxState {
  hasLogin?: boolean;
  state?: IReduxUserState;
  onTapBuy?: (code: string) => void;
}

export const TMDetailCtx = React.createContext<ITMDetailCtxState>({});

// SSR 获取 Query参数
export const getServerSideProps: GetServerSideProps = async (context) => {
  // 商品编号
  let code: string = context.query.code as string;
  if (!code || !_.isString(code)) {
    return { notFound: true };
  }

  // 请求数据
  const response = await Api.detail(code);
  if (!response.success) {
    return { notFound: true };
  }

  return {
    props: {
      modelData: response.data?.data,
    },
  };
};

const TMDetailPage: NextPage = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const model: TMModel = new TMModel(props.modelData);
  const router = useRouter();
  const { state, hasLogin } = useAppSelector((state) => ({
    state: state.user,
    hasLogin: state._persist?.rehydrated && state.user.user !== undefined,
  }));
  const [loading, setLoading] = useState(false);
  const [ctxValue, setCtxValue] = useState<Omit<ITMDetailCtxState, "onTapBuy">>();

  useEffect(() => {
    setCtxValue({
      state,
      hasLogin,
    });
  }, [state, hasLogin]);

  // 请求 - 购买商标，创建订单
  const requestBuy = useCallback(
    async (code: string) => {
      setLoading(true);
      const response = await Api.Order.create(code);
      setLoading(false);
      if (!response.success) {
        message.error(response.errorDescribe);
        return;
      }
      message.success("下单成功", 1, () => {
        router.push(AppRouter.User.Center.order);
      });
    },
    [router]
  );

  // 点击了购买商标
  const onTapBuy = useCallback(
    (code: string) => {
      requestBuy(code);
    },
    [requestBuy]
  );

  return (
    <div className={style.page}>
      <HYHead
        title={model.name}
        description={`${model.name},${model.uiCategory},${model.uiScope}`}
        keywords={`${model.name},${model.uiCategory}`}
      />
      <div className={style.naviHeader}>
        <PageHeader mode="black" />
      </div>
      <Spin spinning={loading}>
        <div className={style.section}>
          <div
            className={`${style.breadMenu} flex-row flex-cross-axis-center flex-main-axis-between`}
          >
            <Breadcrumb separator=">">
              <Breadcrumb.Item href={AppRouter.User.Center.home}>
                <HomeOutlined style={{ marginRight: 5 }} />
                首页
              </Breadcrumb.Item>
              <Breadcrumb.Item href={AppRouter.TM.transfer({ c: model.classify })}>
                {model.uiCategory}
              </Breadcrumb.Item>
              <Breadcrumb.Item>{model.name}</Breadcrumb.Item>
            </Breadcrumb>
            <TMSearchTopInfo style={{ padding: 0, border: "none" }} />
          </div>
          <TMDetailCtx.Provider value={{ ...ctxValue, onTapBuy: onTapBuy }}>
            <TMDetailHeaderInfo model={model} />
          </TMDetailCtx.Provider>
          <TMDetailBasicInfo model={model} />
          <TMDetailTransferData />
          <TMTransferNotFound />
          <TMTransferRecommend hasLogin={hasLogin} />
        </div>
      </Spin>
      <PageFooter />
    </div>
  );
};

export default TMDetailPage;
